<template>
  <main>
    <div class="game-container">
      <div class="game-info">
        <div class="score-board">
          <p>Score: <span class="score">{{ score }}</span></p>
          <p>High Score: <span class="high-score">{{ highScore }}</span></p>
        </div>
        <div class="canvas-wrapper"
          @touchstart="handleTouchStart"
          @touchend="handleTouchEnd">
          <canvas
            ref="gameCanvas"
            class="game-canvas"
          ></canvas>
        </div>
        <div class="game-buttons">
          <p v-if="gameOver" class="game-over">Game Over!</p>
          <p v-if="!gameStarted && !gameOver" class="start-message">Press SPACE or tap START to begin</p>
          <button @click="startGame" class="btn btn-primary">{{ gameStarted && !gameOver ? 'Pause' : 'Start' }}</button>
          <button @click="resetGame" class="btn btn-secondary">Reset</button>
        </div>
        <div class="mobile-controls" v-if="isMobile">
          <div class="arrow-buttons">
            <button class="arrow-btn up" @click="setDirection({x: 0, y: -1})" aria-label="Up">▲</button>
            <div class="horizontal-arrows">
              <button class="arrow-btn left" @click="setDirection({x: -1, y: 0})" aria-label="Left">◄</button>
              <button class="arrow-btn down" @click="setDirection({x: 0, y: 1})" aria-label="Down">▼</button>
              <button class="arrow-btn right" @click="setDirection({x: 1, y: 0})" aria-label="Right">►</button>
            </div>
          </div>
        </div>
        <div class="instructions">
          <p v-if="!isMobile"><strong>Controls:</strong> Arrow Keys, WASD, or Swipe</p>
          <p v-else><strong>Controls:</strong> Tap arrows or swipe on the game</p>
          <p>Eat the red food to grow and earn points!</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'SnakeGame',
  data() {
    return {
      gridSize: 20,
      canvasSize: 400,
      snake: [{ x: 10, y: 10 }],
      food: { x: 15, y: 15 },
      direction: { x: 1, y: 0 },
      nextDirection: { x: 1, y: 0 },
      score: 0,
      highScore: localStorage.getItem('snakeHighScore') || 0,
      gameStarted: false,
      gameOver: false,
      gameLoopId: null,
      speed: 100,
      isMobile: false,
      touchStartX: 0,
      touchStartY: 0,
      minSwipeDistance: 30
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeyPress);
    this.isMobile = this.checkIfMobile();
    window.addEventListener('resize', () => {
      this.isMobile = this.checkIfMobile();
      this.resizeCanvas();
    });
    this.resizeCanvas();
    this.draw();
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeyPress);
    if (this.gameLoopId) clearInterval(this.gameLoopId);
  },
  methods: {
    checkIfMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             window.innerWidth <= 768;
    },
    resizeCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.gameCanvas;
        if (!canvas) return;

        const container = canvas.parentElement;
        const maxSize = Math.min(
          container.clientWidth - 20,
          window.innerHeight * 0.5,
          400
        );

        canvas.width = maxSize;
        canvas.height = maxSize;
        this.canvasSize = maxSize;
        this.draw();
      });
    },
    handleTouchStart(e) {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
    },
    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;

      const diffX = touchEndX - this.touchStartX;
      const diffY = touchEndY - this.touchStartY;

      if (!this.gameStarted || this.gameOver) return;

      const absDiffX = Math.abs(diffX);
      const absDiffY = Math.abs(diffY);

      if (absDiffX < this.minSwipeDistance && absDiffY < this.minSwipeDistance) return;

      let newDir = null;
      if (absDiffX > absDiffY) {
        newDir = diffX > 0 ? { x: 1, y: 0 } : { x: -1, y: 0 };
      } else {
        newDir = diffY > 0 ? { x: 0, y: 1 } : { x: 0, y: -1 };
      }

      if (newDir && !(newDir.x === -this.direction.x && newDir.y === -this.direction.y)) {
        this.nextDirection = newDir;
      }
    },
    setDirection(newDir) {
      if (!this.gameStarted || this.gameOver) return;
      if (!(newDir.x === -this.direction.x && newDir.y === -this.direction.y)) {
        this.nextDirection = newDir;
      }
    },
    handleKeyPress(e) {
      const key = e.key.toLowerCase();

      if (key === ' ') {
        e.preventDefault();
        this.startGame();
        return;
      }

      if (!this.gameStarted || this.gameOver) return;

      const directionMap = {
        'arrowup': { x: 0, y: -1 },
        'w': { x: 0, y: -1 },
        'arrowdown': { x: 0, y: 1 },
        's': { x: 0, y: 1 },
        'arrowleft': { x: -1, y: 0 },
        'a': { x: -1, y: 0 },
        'arrowright': { x: 1, y: 0 },
        'd': { x: 1, y: 0 }
      };

      const newDir = directionMap[key];
      if (newDir) {
        e.preventDefault();
        // Prevent reversing into itself
        if (!(newDir.x === -this.direction.x && newDir.y === -this.direction.y)) {
          this.nextDirection = newDir;
        }
      }
    },
    startGame() {
      if (this.gameOver) return;

      this.gameStarted = !this.gameStarted;

      if (this.gameStarted) {
        this.gameLoopId = setInterval(() => this.updateGame(), this.speed);
      } else {
        clearInterval(this.gameLoopId);
      }
    },
    resetGame() {
      clearInterval(this.gameLoopId);
      this.snake = [{ x: 10, y: 10 }];
      this.food = this.generateFood();
      this.direction = { x: 1, y: 0 };
      this.nextDirection = { x: 1, y: 0 };
      this.score = 0;
      this.gameStarted = false;
      this.gameOver = false;
      this.draw();
    },
    updateGame() {
      this.direction = this.nextDirection;

      // Calculate new head position
      const head = this.snake[0];
      const newHead = {
        x: head.x + this.direction.x,
        y: head.y + this.direction.y
      };

      // Check wall collision
      if (newHead.x < 0 || newHead.x >= this.gridSize ||
          newHead.y < 0 || newHead.y >= this.gridSize) {
        this.endGame();
        return;
      }

      // Check self collision
      if (this.snake.some(segment => segment.x === newHead.x && segment.y === newHead.y)) {
        this.endGame();
        return;
      }

      // Add new head
      this.snake.unshift(newHead);

      // Check food collision
      if (newHead.x === this.food.x && newHead.y === this.food.y) {
        this.score += 10;
        this.food = this.generateFood();
      } else {
        // Remove tail if no food eaten
        this.snake.pop();
      }

      this.draw();
    },
    generateFood() {
      let newFood;
      let isOnSnake;

      do {
        newFood = {
          x: Math.floor(Math.random() * this.gridSize),
          y: Math.floor(Math.random() * this.gridSize)
        };
        isOnSnake = this.snake.some(segment =>
          segment.x === newFood.x && segment.y === newFood.y
        );
      } while (isOnSnake);

      return newFood;
    },
    endGame() {
      clearInterval(this.gameLoopId);
      this.gameStarted = false;
      this.gameOver = true;

      if (this.score > this.highScore) {
        this.highScore = this.score;
        localStorage.setItem('snakeHighScore', this.highScore);
      }
    },
    draw() {
      const canvas = this.$refs.gameCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      const pixelSize = this.canvasSize / this.gridSize;

      // Clear canvas
      ctx.fillStyle = '#f0f0f0';
      ctx.fillRect(0, 0, this.canvasSize, this.canvasSize);

      // Draw grid
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= this.gridSize; i++) {
        const pos = i * pixelSize;
        ctx.beginPath();
        ctx.moveTo(pos, 0);
        ctx.lineTo(pos, this.canvasSize);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, pos);
        ctx.lineTo(this.canvasSize, pos);
        ctx.stroke();
      }

      // Draw snake
      this.snake.forEach((segment, index) => {
        ctx.fillStyle = index === 0 ? '#2ecc71' : '#27ae60';
        ctx.fillRect(
          segment.x * pixelSize + 1,
          segment.y * pixelSize + 1,
          pixelSize - 2,
          pixelSize - 2
        );
      });

      // Draw food
      ctx.fillStyle = '#e74c3c';
      ctx.fillRect(
        this.food.x * pixelSize + 2,
        this.food.y * pixelSize + 2,
        pixelSize - 4,
        pixelSize - 4
      );
    }
  }
}
</script>

<style scoped>
main {
  padding: 10px;
}

.game-container {
  display: flex;
  justify-content: center;
}

.game-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  width: 100%;
}

.score-board {
  display: flex;
  gap: 30px;
  font-size: 1.1rem;
  font-weight: bold;
}

.score,
.high-score {
  color: #2ecc71;
  margin-left: 5px;
}

.canvas-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  touch-action: none;
}

.game-canvas {
  border: 2px solid #333;
  background-color: #f0f0f0;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: block;
  max-width: 100%;
  height: auto;
}

.game-buttons {
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: center;
}

.btn {
  padding: 12px 24px;
  font-size: 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-weight: bold;
  min-width: 100px;
}

.btn-primary {
  background-color: #2ecc71;
  color: white;
}

.btn-primary:hover,
.btn-primary:active {
  background-color: #27ae60;
}

.btn-secondary {
  background-color: #3498db;
  color: white;
}

.btn-secondary:hover,
.btn-secondary:active {
  background-color: #2980b9;
}

.game-over {
  color: #e74c3c;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
}

.start-message {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin: 0;
}

.mobile-controls {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

.arrow-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
}

.horizontal-arrows {
  display: flex;
  gap: 10px;
}

.arrow-btn {
  width: 50px;
  height: 50px;
  font-size: 1.2rem;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  font-weight: bold;
  user-select: none;
}

.arrow-btn:hover {
  background-color: #2980b9;
  transform: scale(1.1);
}

.arrow-btn:active {
  background-color: #1a5276;
  transform: scale(0.95);
}

.instructions {
  text-align: center;
  color: #7f8c8d;
  font-size: 0.85rem;
  margin: 0;
}

.instructions p {
  margin: 4px 0;
}

@media (max-width: 768px) {
  main {
    padding: 8px;
    padding-top: 70px;
  }

  .score-board {
    gap: 20px;
    font-size: 1rem;
  }

  .game-canvas {
    max-width: 100%;
  }

  .btn {
    padding: 10px 20px;
    font-size: 0.95rem;
    min-width: 80px;
  }

  .arrow-btn {
    width: 45px;
    height: 45px;
    font-size: 1rem;
  }

  .instructions {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  main {
    padding: 4px 4px 4px 4px;
    padding-top: 75px;
  }

  h1 {
    font-size: 1.2rem;
  }

  .score-board {
    flex-direction: column;
    gap: 8px;
    font-size: 0.9rem;
  }

  .btn {
    padding: 8px 16px;
    font-size: 0.9rem;
    min-width: 70px;
  }

  .arrow-btn {
    width: 40px;
    height: 40px;
    font-size: 0.9rem;
  }

  .horizontal-arrows {
    gap: 8px;
  }

  .arrow-buttons {
    gap: 8px;
  }

  .instructions {
    font-size: 0.75rem;
  }
}
</style>
